/**
 * TagadaPay External Page Tracker — thin wrapper around the CDN bundle.
 *
 * Loads the script asynchronously the FIRST time `initTagada()` is called,
 * then exposes safe no-op helpers for the rest of the app to call without
 * worrying about boot order or whether credentials are configured.
 *
 * Docs: https://docs.tagada.io/developer-tools/web-integration/external-page-tracker
 *
 * Env vars (all REACT_APP_* so CRA exposes them to the browser):
 *   REACT_APP_TAGADA_STORE_ID     (e.g. store_019dd15cdb5b)
 *   REACT_APP_TAGADA_ACCOUNT_ID   (e.g. acc_4ff19578aaa2)
 *   REACT_APP_TAGADA_STEP_ID      (e.g. step_abc123 — the funnel step the
 *                                  storefront represents)
 *   REACT_APP_TAGADA_FUNNEL_ID    (optional — usually auto-detected)
 *   REACT_APP_TAGADA_DEBUG=true   (optional)
 *
 * If any of the three required IDs are missing, every export is a no-op so
 * the rest of the app keeps working unchanged.
 */
const CDN =
  'https://cdn.jsdelivr.net/npm/@tagadapay/plugin-sdk/dist/external-tracker.min.js';

let loadingPromise = null;
let initPromise = null;
let isReady = false;

const cfg = () => ({
  storeId: process.env.REACT_APP_TAGADA_STORE_ID,
  accountId: process.env.REACT_APP_TAGADA_ACCOUNT_ID,
  stepId: process.env.REACT_APP_TAGADA_STEP_ID,
  funnelId: process.env.REACT_APP_TAGADA_FUNNEL_ID,
  debug: process.env.REACT_APP_TAGADA_DEBUG === 'true',
});

const isConfigured = () => {
  const { storeId, accountId, stepId } = cfg();
  return Boolean(storeId && accountId && stepId);
};

const loadScript = () => {
  if (typeof window === 'undefined') return Promise.resolve(false);
  if (window.TagadaTracker) return Promise.resolve(true);
  if (loadingPromise) return loadingPromise;
  loadingPromise = new Promise((resolve) => {
    const s = document.createElement('script');
    s.src = CDN;
    s.async = true;
    s.onload = () => resolve(Boolean(window.TagadaTracker));
    s.onerror = () => resolve(false);
    document.head.appendChild(s);
  });
  return loadingPromise;
};

export const initTagada = async () => {
  if (!isConfigured()) return null;
  if (initPromise) return initPromise;
  initPromise = (async () => {
    const ok = await loadScript();
    if (!ok || !window.TagadaTracker) return null;
    const c = cfg();
    return new Promise((resolve) => {
      try {
        window.TagadaTracker.init({
          storeId: c.storeId,
          accountId: c.accountId,
          stepId: c.stepId,
          funnelId: c.funnelId,
          debug: c.debug,
          onReady: (session) => {
            isReady = true;
            resolve(session);
          },
          onError: (err) => {
            // eslint-disable-next-line no-console
            console.warn('TagadaTracker init failed:', err && err.message);
            resolve(null);
          },
        });
      } catch (err) {
        // eslint-disable-next-line no-console
        console.warn('TagadaTracker init threw:', err);
        resolve(null);
      }
    });
  })();
  return initPromise;
};

export const destroyTagada = () => {
  try {
    if (typeof window !== 'undefined' && window.TagadaTracker && isReady) {
      window.TagadaTracker.destroy();
    }
  } catch {
    /* no-op */
  }
  isReady = false;
  initPromise = null;
};

export const trackTagadaEvent = (name, data) => {
  try {
    if (
      typeof window !== 'undefined' &&
      window.TagadaTracker &&
      window.TagadaTracker.isReady &&
      window.TagadaTracker.isReady()
    ) {
      window.TagadaTracker.trackEvent({ name, data: data || {} });
    }
  } catch {
    /* swallow — analytics must never break checkout */
  }
};

export const tagadaNavigate = async (opts) => {
  if (
    typeof window === 'undefined' ||
    !window.TagadaTracker ||
    !(window.TagadaTracker.isReady && window.TagadaTracker.isReady())
  ) {
    return null;
  }
  try {
    return await window.TagadaTracker.navigate(opts || {});
  } catch (err) {
    // eslint-disable-next-line no-console
    console.warn('TagadaTracker.navigate failed:', err);
    return null;
  }
};

export const tagadaBuildUrl = (baseUrl, params) => {
  try {
    if (
      typeof window !== 'undefined' &&
      window.TagadaTracker &&
      window.TagadaTracker.isReady &&
      window.TagadaTracker.isReady()
    ) {
      return window.TagadaTracker.buildUrl(baseUrl, params || {});
    }
  } catch {
    /* no-op */
  }
  return baseUrl;
};

export const tagadaSession = () => {
  try {
    if (
      typeof window !== 'undefined' &&
      window.TagadaTracker &&
      window.TagadaTracker.getSession
    ) {
      return window.TagadaTracker.getSession();
    }
  } catch {
    /* no-op */
  }
  return null;
};

export const tagadaConfigured = isConfigured;


/**
 * Re-initialize the TagadaPay tracker for the Thank You / Payment Success
 * page so the conversion is attributed to the dedicated funnel step instead
 * of the storefront step.
 *
 * Call this from a useEffect on `/payment/success` — it awaits the global
 * storefront init (so destroy() has something to tear down), then re-inits
 * a fresh session with the Thank You step. Returns the new session.
 *
 * Matches the snippet TagadaPay's docs recommend for the Thank You step:
 *   TagadaTracker.init({ storeId, accountId, funnelId, stepId, stepName: 'Thank You' })
 */
export const initTagadaThankYou = async () => {
  const c = cfg();
  const thankYouStepId = process.env.REACT_APP_TAGADA_THANK_YOU_STEP_ID;
  if (!c.storeId || !c.accountId || !thankYouStepId) return null;

  // 1. Wait for the global storefront tracker to finish initializing first
  //    (it was kicked off by TagadaTrackerMount at app boot). Without this
  //    await, destroy() runs against a half-initialized SDK and the
  //    subsequent init() either no-ops or races and we end up still
  //    reporting the storefront step.
  await initTagada();

  // 2. Tear down the storefront tracker so the SDK accepts a fresh init().
  destroyTagada();

  // 3. Ensure the SDK bundle is loaded (cheap if already cached).
  const ok = await loadScript();
  if (!ok || !window.TagadaTracker) return null;

  // 4. Init the Thank You step. Same shape as the snippet in TagadaPay's
  //    docs but driven by env vars for staging/prod parity.
  return new Promise((resolve) => {
    try {
      window.TagadaTracker.init({
        storeId: c.storeId,
        accountId: c.accountId,
        funnelId: c.funnelId,
        stepId: thankYouStepId,
        stepName: 'Thank You',
        debug: c.debug,
        onReady: (session) => {
          isReady = true;
          resolve(session);
        },
        onError: (err) => {
          // eslint-disable-next-line no-console
          console.warn('TagadaTracker Thank You init failed:', err && err.message);
          resolve(null);
        },
      });
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn('TagadaTracker Thank You init threw:', err);
      resolve(null);
    }
  });
};
